import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { Box, Typography, IconButton, Tooltip } from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import SupplyApplyPills from "../SupplyApplyPills";
import { renderCurrencyIcon } from "../../utils/CurrencyUtils";
import { formattedAmountWithoutAed } from "../../utils/CommonFunction";
import { uploadBOQImage, deleteFile } from "../../services/budgetService";
import { getBoqDisplayImage, isPlaceholderBoqImage } from "../../constants/boq";

// Inline BOQ row — same single-line layout as the admin portal's BOQItemRow.
// - read-only by default
// - pass onEdit / onDelete for edit/delete actions (edit typically toggles the
//   parent's inline edit form, like the admin)
// - pass editableImage to allow uploading/removing the item image inline
// - hidePrice hides the Unit Price / Total Price columns

const AmountText = ({ value }) => {
  if (value == null || value === "" || Number.isNaN(Number(value))) return <>-</>;
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 2 }}>
      {renderCurrencyIcon("black-xs-small")}
      {formattedAmountWithoutAed(Number(value) || 0)}
    </span>
  );
};

const Cell = ({ label, children, flex = 1, right = false }) => (
  <Box sx={{ flex, minWidth: 0 }}>
    <Typography
      sx={{
        fontSize: 10,
        fontWeight: 600,
        color: "#6B7280",
        textTransform: "uppercase",
        letterSpacing: "0.03em",
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        textAlign: right ? "right" : "left",
      }}
    >
      {label}
    </Typography>
    <Typography
      sx={{
        fontSize: 12,
        fontWeight: 500,
        color: "#111827",
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        textAlign: right ? "right" : "left",
        mt: 0.25,
      }}
    >
      {children}
    </Typography>
  </Box>
);

const val = (...vals) => vals.find((v) => v != null && v !== "");

const BoqItemRow = ({
  boq,
  onEdit,
  onDelete,
  disabled = false,
  hidePrice = false,
  editableImage = false,
  onImageClick,
  onBudgetUpdated,
}) => {
  const name = val(boq.name, boq.item_name) || "-";
  const type = val(boq.boq_type, boq.type);
  const unit = val(boq.material_unit, boq.unit_of_measurement) || "-";
  const isActivities = String(type || "").toLowerCase() === "activities";
  const qty = val(boq.contractor_qty, boq.quantity, boq.days, boq.qty);
  const unitCost = val(boq.material_unit_price, boq.unit_cost, boq.manpower_rate, boq.quoted_price);
  const totalCost = val(boq.total_item, boq.total_cost);
  const unitPrice = val(boq.unit_price);
  const totalPrice = val(boq.price, boq.total_price);
  const leadTime = val(boq.lead_time);
  const spec = val(boq.specification);
  const supply = val(boq.supply_type);

  // Image: read-only rows just show whatever url is on the item; editable rows
  // manage it through getBoqDisplayImage (+ placeholder fallback) like the admin.
  const [imageObj, setImageObj] = useState(() =>
    editableImage ? getBoqDisplayImage(boq) : null
  );
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [hovered, setHovered] = useState(false);
  useEffect(() => {
    if (editableImage) setImageObj(getBoqDisplayImage(boq));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boq.image, editableImage]);

  const staticImage = val(boq.image_url, boq.image?.[0]?.image, boq.image);
  const imageUrl = editableImage ? imageObj?.image : staticImage;

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !boq?.id) {
      if (e.target) e.target.value = "";
      return;
    }
    try {
      setUploading(true);
      const data = await uploadBOQImage(boq.id, file);
      const images = data?.images ?? data?.data ?? [];
      const first = images[0];
      const url = first?.image ?? first?.path;
      if (data?.success !== false && url) {
        toast.success("Image uploaded successfully");
        setImageObj({ id: first?.id ?? imageObj?.id ?? null, image: url });
        onBudgetUpdated?.();
      } else {
        toast.error("Failed to upload image");
      }
    } catch (err) {
      console.error("Error uploading BOQ image:", err);
      toast.error("Failed to upload image");
    } finally {
      setUploading(false);
      if (e.target) e.target.value = "";
    }
  };

  const handleDeleteImage = async (e) => {
    e?.stopPropagation?.();
    if (!imageObj) return;
    if (isPlaceholderBoqImage(imageObj)) {
      setImageObj(null);
      onBudgetUpdated?.();
      return;
    }
    if (imageObj.id == null) return;
    try {
      setDeleting(true);
      const data = await deleteFile(imageObj.id);
      if (data?.success !== false) {
        toast.success("Image deleted successfully");
        setImageObj(null);
        onBudgetUpdated?.();
      } else {
        toast.error("Failed to delete image");
      }
    } catch (err) {
      console.error("Error deleting BOQ image:", err);
      toast.error("Failed to delete image");
    } finally {
      setDeleting(false);
    }
  };

  const fileInputId = `boq-row-image-${boq.id}`;
  const canManageImage = editableImage && !disabled;

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        px: 1.5,
        py: 1,
        border: "1px solid #E5E7EB",
        borderRadius: "8px",
        backgroundColor: "#fff",
      }}
    >
      {/* Image */}
      <Box
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        sx={{
          position: "relative",
          width: 44,
          height: 44,
          flexShrink: 0,
          borderRadius: "6px",
          overflow: "hidden",
          backgroundColor: "#E5E7EB",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <input
          type="file"
          accept="image/*"
          onChange={handleUpload}
          style={{ display: "none" }}
          id={fileInputId}
          disabled={uploading}
        />
        {imageUrl ? (
          <>
            <img
              src={imageUrl}
              alt=""
              onClick={() => onImageClick?.(imageUrl)}
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                cursor: onImageClick ? "pointer" : "default",
              }}
              onError={(e) => {
                e.currentTarget.style.display = "none";
              }}
            />
            {canManageImage && hovered && (
              <IconButton
                size="small"
                onClick={handleDeleteImage}
                disabled={deleting}
                sx={{
                  position: "absolute",
                  top: -2,
                  right: -2,
                  p: "1px",
                  backgroundColor: "#EF4444",
                  color: "#fff",
                  "&:hover": { backgroundColor: "#DC2626" },
                }}
              >
                <CloseIcon sx={{ fontSize: 12 }} />
              </IconButton>
            )}
          </>
        ) : canManageImage ? (
          <label
            htmlFor={fileInputId}
            style={{
              display: "flex",
              width: "100%",
              height: "100%",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
            }}
          >
            <AddIcon sx={{ fontSize: 18, color: "#6B7280" }} />
          </label>
        ) : (
          <ImageOutlinedIcon sx={{ fontSize: 18, color: "#9CA3AF" }} />
        )}
      </Box>

      {/* Name + supply pills */}
      <Box sx={{ flex: 2, minWidth: 0 }}>
        <Typography
          sx={{
            fontSize: 14,
            fontWeight: 500,
            color: "#111827",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
          title={name}
        >
          {name}
        </Typography>
        {supply && (
          <Box sx={{ mt: 0.25 }}>
            <SupplyApplyPills supplyType={supply} />
          </Box>
        )}
      </Box>

      {/* Cells */}
      <Cell label="Type">
        {type ? (
          <span
            style={{
              color: "#1e40af",
              backgroundColor: "#dbeafe",
              padding: "1px 6px",
              borderRadius: 4,
              fontSize: 11,
            }}
          >
            {type}
          </span>
        ) : (
          "-"
        )}
      </Cell>
      <Cell label="Unit">{unit}</Cell>
      <Cell label={isActivities ? "Days" : "Qty"}>{qty != null ? qty : "-"}</Cell>
      <Cell label={isActivities ? "Manpower Rate" : "Unit Cost"}>
        <AmountText value={unitCost} />
      </Cell>
      <Cell label="Total Cost">
        <AmountText value={totalCost} />
      </Cell>
      <Cell label="Lead Time">{leadTime != null ? leadTime : "—"}</Cell>
      <Cell label="Specification" flex={1.5}>
        <span title={spec || undefined}>{spec || "—"}</span>
      </Cell>

      {/* Actions */}
      {/* {(onEdit || onDelete) && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexShrink: 0 }}>
          {onEdit && (
            <Tooltip title="Edit">
              <span>
                <IconButton
                  size="small"
                  disabled={disabled}
                  onClick={() => !disabled && onEdit(boq)}
                  sx={{ color: "#4B5563" }}
                >
                  <EditOutlinedIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </span>
            </Tooltip>
          )}
          {onDelete && (
            <Tooltip title="Delete">
              <span>
                <IconButton
                  size="small"
                  disabled={disabled}
                  onClick={() => !disabled && onDelete(boq)}
                  sx={{ color: "#DC2626" }}
                >
                  <CloseIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </span>
            </Tooltip>
          )}
        </Box>
      )} */}
    </Box>
  );
};

export default BoqItemRow;
